import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"
import rehypeSlug from "rehype-slug"
import { remarkTableWidths } from "@/lib/remark-table-widths"
import { SEO } from "@/components/SEO"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/theme-toggle"
import { getDeckBySlug } from "@/config/decks"
import { FEATURES } from "@/config/features"
import { ChevronLeft, ChevronRight } from "lucide-react"
import welephantLogo from "@/assets/tokens/welephant.svg"
import "@/components/deck/deck-theme.css"

interface Slide {
  title: string
  hidden: boolean
  content: string
}

function parseSlides(markdown: string): Slide[] {
  return markdown
    .split(/(?=^# )/m)
    .filter((s) => s.trim())
    .map((raw) => {
      const firstLine = raw.split("\n")[0]
      const match = firstLine.match(/^# (.+?)(?:\s*\{hidden\})?\s*$/)
      const title = match?.[1]?.trim() || "Untitled"
      const hidden = /\{hidden\}/.test(firstLine)
      const content = hidden ? raw.replace(/^# .+\n/, "") : raw
      return { title, hidden, content }
    })
}

export function DeckPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const deck = slug ? getDeckBySlug(slug) : undefined

  const [markdown, setMarkdown] = useState("")
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)

  // Redirect if slug not found
  useEffect(() => {
    if (slug && !deck) {
      navigate("/decks", { replace: true })
    }
  }, [slug, deck, navigate])

  // Fetch markdown
  useEffect(() => {
    if (!deck) return
    setLoading(true)
    fetch(deck.path)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch ${deck.path}`)
        return res.text()
      })
      .then((text) => {
        setMarkdown(text)
        setCurrentIndex(0)
      })
      .catch(() => setMarkdown("# Error\n\nFailed to load presentation."))
      .finally(() => setLoading(false))
  }, [deck])

  const slides = useMemo(() => {
    const parsed = parseSlides(markdown)
    if (parsed.length === 0) return parsed
    const disclaimerSlide: Slide = {
      title: "Disclaimer",
      hidden: false,
      content: `# Disclaimer

The information provided is for informational and educational purposes only and does not constitute financial, investment, legal, or tax advice. All opinions expressed are solely those of the WELEPHANT team and are based on publicly available information and personal analysis.

We may hold positions in the cryptocurrencies, tokens, or protocols discussed, and these positions may change at any time without notice. WELEPHANT.XYZ is a ${FEATURES.mining ? "decentralized prediction mining game" : "decentralized cryptocurrency protocol"} involving cryptocurrency — participation carries risk, including the potential loss of all funds deposited. Smart contracts may contain bugs or vulnerabilities, blockchain transactions are irreversible, and cryptocurrency values are highly volatile. Past performance is not indicative of future results.

Nothing published here constitutes a solicitation or recommendation to participate in any protocol or purchase any digital asset. Any decisions you make should be based on your own research and risk tolerance. Please review our [Terms of Service](/terms) for full details.`,
    }
    return [disclaimerSlide, ...parsed]
  }, [markdown])

  const goNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(i + 1, slides.length - 1))
  }, [slides.length])

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => Math.max(i - 1, 0))
  }, [])

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault()
        goNext()
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault()
        goPrev()
      }
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [goNext, goPrev])

  // Touch swipe navigation
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  useEffect(() => {
    function handleTouchStart(e: TouchEvent) {
      touchStartX.current = e.touches[0].clientX
      touchStartY.current = e.touches[0].clientY
    }
    function handleTouchEnd(e: TouchEvent) {
      if (touchStartX.current === null || touchStartY.current === null) return
      const dx = e.changedTouches[0].clientX - touchStartX.current
      const dy = e.changedTouches[0].clientY - touchStartY.current
      touchStartX.current = null
      touchStartY.current = null
      if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return
      if (dx < 0) goNext()
      else goPrev()
    }
    window.addEventListener("touchstart", handleTouchStart)
    window.addEventListener("touchend", handleTouchEnd)
    return () => {
      window.removeEventListener("touchstart", handleTouchStart)
      window.removeEventListener("touchend", handleTouchEnd)
    }
  }, [goNext, goPrev])

  if (!deck) return null

  const slide = slides[currentIndex]
  const prevSlide = currentIndex > 0 ? slides[currentIndex - 1] : null
  const nextSlide = currentIndex < slides.length - 1 ? slides[currentIndex + 1] : null

  return (
    <>
      <SEO title={deck.title} url={`/decks/${deck.slug}`} description={deck.description} />
      <div className="relative flex min-h-screen flex-col">
        {/* Simplified header */}
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container flex h-14 items-center">
            <Link to="/decks" className="flex items-center space-x-2">
              <img src={welephantLogo} alt="WELEPHANT" className="h-6 w-6" />
              <span className="text-lg font-bold tracking-tight">WELEPHANT</span>
            </Link>
            <div className="ml-auto">
              <ThemeToggle />
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 flex items-center">
          <div className="container py-6">
            <div className="max-w-4xl mx-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <div className="text-muted-foreground">Loading...</div>
                  </div>
                ) : slide ? (
                  <>
                    <article className="prose prose-neutral dark:prose-invert max-w-full [overflow-wrap:anywhere]">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkTableWidths]}
                        rehypePlugins={[rehypeRaw, rehypeSlug]}
                        components={{
                          h1: ({ children, id }) => (
                            <h1 id={id} className="text-3xl font-bold tracking-tight mb-4 scroll-mt-32">
                              {children}
                            </h1>
                          ),
                          h2: ({ children, id }) => (
                            <h2 id={id} className="text-2xl font-semibold mt-8 mb-4 border-b pb-2 scroll-mt-32">
                              {children}
                            </h2>
                          ),
                          h3: ({ children, id }) => (
                            <h3 id={id} className="text-xl font-semibold mt-6 mb-3 scroll-mt-32">
                              {children}
                            </h3>
                          ),
                          p: ({ children }) => (
                            <p className="text-muted-foreground leading-7 mb-4">{children}</p>
                          ),
                          ul: ({ children }) => (
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">{children}</ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="list-decimal pl-6 space-y-2 text-muted-foreground mb-4">{children}</ol>
                          ),
                          li: ({ children }) => <li className="text-muted-foreground">{children}</li>,
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground my-4">
                              {children}
                            </blockquote>
                          ),
                          code: ({ className, children }) => {
                            const isInline = !className
                            return isInline ? (
                              <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
                            ) : (
                              <code className="block bg-muted p-4 rounded-lg text-sm font-mono overflow-x-auto my-4">
                                {children}
                              </code>
                            )
                          },
                          pre: ({ children }) => (
                            <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4">{children}</pre>
                          ),
                          table: ({ children, node }) => {
                            const widthsAttr = node?.properties?.["data-col-widths"] as string | undefined
                            const widths = widthsAttr?.split(",").map((w) => (w ? parseInt(w, 10) : null))
                            return (
                              <div className="overflow-x-auto my-4 max-w-full">
                                <table className="min-w-full border-collapse table-fixed">
                                  {widths && widths.some((w) => w !== null) && (
                                    <colgroup>
                                      {widths.map((w, i) => (
                                        <col key={i} style={w ? { width: `${w}%` } : undefined} />
                                      ))}
                                    </colgroup>
                                  )}
                                  {children}
                                </table>
                              </div>
                            )
                          },
                          thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
                          th: ({ children }) => (
                            <th className="border px-4 py-2 text-left font-semibold">{children}</th>
                          ),
                          td: ({ children }) => (
                            <td className="border px-4 py-2 text-muted-foreground">{children}</td>
                          ),
                          hr: () => <hr className="my-8 border-border" />,
                          a: ({ href, children }) => (
                            <a
                              href={href}
                              className="text-primary hover:underline"
                              target={href?.startsWith("http") ? "_blank" : undefined}
                              rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
                            >
                              {children}
                            </a>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold text-foreground">{children}</strong>
                          ),
                          img: ({ src, alt }) => (
                            <img src={src} alt={alt || ""} className="max-w-full h-auto rounded-lg my-4" />
                          ),
                        }}
                      >
                        {slide.content}
                      </ReactMarkdown>
                    </article>
                  </>
                ) : (
                  <div className="text-muted-foreground text-center py-8">No slides found.</div>
                )}

              {/* Nav buttons + progress dots in one row */}
              {!loading && slides.length > 0 && (
                <div className="flex items-center justify-between mt-4">
                  <div>
                    {prevSlide && (
                      <Button variant="ghost" size="icon" onClick={(e) => { e.currentTarget.blur(); goPrev() }}>
                        <ChevronLeft />
                      </Button>
                    )}
                  </div>
                  {slides.length > 1 && (
                    <div className="flex gap-1.5">
                      {slides.map((_, i) => (
                        <button
                          key={i}
                          onClick={(e) => { e.currentTarget.blur(); setCurrentIndex(i) }}
                          className={`h-2 rounded-full transition-all ${
                            i === currentIndex
                              ? "w-6 bg-foreground"
                              : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                          }`}
                          aria-label={`Go to slide ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}
                  <div>
                    {nextSlide && (
                      <Button variant="ghost" size="icon" onClick={(e) => { e.currentTarget.blur(); goNext() }}>
                        <ChevronRight />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
